import { initComponent } from './core/init-component';

// READ MORE label follows the cursor on md+ (mirrors shop-the-look.ts). Below 1024px the
// server-rendered filled button is the affordance and this behaviour is inert. The whole
// block is the click target; clicks are forwarded to the inner <a>/<button> so the existing
// navigation / drawer logic (delegated listener in drawer.ts) is reused with no duplication.
initComponent('.js-promo-block', (el) => {
  const label = el.querySelector<HTMLElement>('.js-promo-readmore');
  if (!label) return;

  const trigger = label.querySelector<HTMLElement>('a, button');
  const isMd = window.matchMedia('(min-width: 1024px)');
  let rafId = 0;
  let resizeTimer: ReturnType<typeof setTimeout>;

  function show() {
    label!.classList.remove('opacity-0');
  }

  function hide() {
    // Keep the current transform so the label fades out in place instead of snapping
    // back to the top-left anchor (opacity-0 hides it; the next show() repositions first).
    label!.classList.add('opacity-0');
  }

  function onMouseMove(evt: MouseEvent) {
    if (!isMd.matches) return;
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      // left edge at cursor X, vertically centred on cursor Y (translate % is relative
      // to the label's own size, so -50% centres regardless of its height).
      label!.style.transform =
        `translate(${evt.clientX - rect.left}px, calc(-50% + ${evt.clientY - rect.top}px))`;
      show();
    });
  }

  function onMouseLeave() {
    cancelAnimationFrame(rafId);
    hide();
  }

  function onClick(evt: MouseEvent) {
    if (!isMd.matches) return;
    // Forwarding fires a synthetic click on `trigger` that bubbles back to this same
    // listener; ignore that re-entry (and any direct click on the trigger) to avoid an
    // infinite loop. A real user click never lands on the trigger (it is pointer-events-none).
    const target = evt.target as Node | null;
    if (trigger && target && (target === trigger || trigger.contains(target))) return;
    trigger?.click();
  }

  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (!isMd.matches) hide();
    }, 150);
  }

  el.addEventListener('mousemove', onMouseMove);
  el.addEventListener('mouseleave', onMouseLeave);
  el.addEventListener('click', onClick);
  window.addEventListener('resize', onResize);

  return {
    unload() {
      el.removeEventListener('mousemove', onMouseMove);
      el.removeEventListener('mouseleave', onMouseLeave);
      el.removeEventListener('click', onClick);
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(rafId);
      clearTimeout(resizeTimer);
    },
  };
});
